import Dexie, { Table } from 'dexie';
import type { IntensityLabel } from './services/sprayService';
import type { NutritionMyWorkItem } from './services/nutritionSessionService';

export type { NutritionMyWorkItem };

export interface ScoutingPoint {
  treeIndex: number;
  pointIndex: number;
  pestId: string;
  timestamp: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
  hardwareTimestamp?: number;
  photoIds?: number[];
}

export interface ScoutingSession {
  id?: number;
  uuid: string; // Unique ID for deduplication
  scouterName: string;
  orchardName: string;
  date: string;
  points: ScoutingPoint[];
  source?: 'manual' | 'field'; // 'field' for app, 'manual' for web form
  metadata?: {
    pests?: {
      [key: string]: {
        count?: number;
        percent?: number;
        level?: string;
      };
    };
    [key: string]: any;
  };
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed' | 'quarantined';
  retryCount?: number;
}

export interface Pest {
  id: string; // This is the _id from MongoDB
  code: string;
  name: string;
  nameI18n?: { en?: string; af?: string };
  category?: 'pest' | 'disease' | 'beneficial' | 'weed' | 'disorder';
  order: number;
  measurementType?: 'percentage' | 'level' | 'count';
  displayPreference?: 'name' | 'alias';
  aliases?: string[];
  // Field Scout Mode config (Slice 1) — incidence-only fields may be absent:
  samplingUnit?: 'point' | 'tree';
  tallyThreshold?: 'any' | '2+' | '3+';
  actionThreshold?: number;
}

export interface Media {
  id?: number;
  sessionId: number;
  data: Blob;
  type: string;
}

export interface Orchard {
  id: string; // MongoDB _id
  orchardName: string;
  farmName: string;
  location?: {
    type: 'Point';
    coordinates: number[];
  };
  boundary?: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  cultivar?: string;
  variety?: string;
  varietyI18n?: { en?: string; af?: string };
  activePetalFallDate?: string | null;
  lastUpdated: string; // ISO string for synchronization tracking
}

export interface PhenologyRecord {
  id?: number;
  orchardId: string;
  date: string;
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed';
  createdAt: string;
}

export interface AppSettings {
  id: 'local';
  retentionDays: number;
}

export interface DashboardSettings {
  id: 'dashboard';
  /** Days since last scouted that still qualifies as green (recently scouted). Default: 3 */
  greenDays: number;
  /** Days since last scouted that qualifies as yellow (needs attention soon). Default: 7 */
  yellowDays: number;
  /** Expected scouting cadence in days for compliance calculation. Default: 7 */
  cadenceDays: number;
  /** Hex color for "green" (fresh) orchards. Default: #27ae60 */
  greenColor?: string;
  /** Hex color for "yellow" (warning) orchards. Default: #f39c12 */
  yellowColor?: string;
  /** Hex color for "red" (stale) orchards. Default: #e74c3c */
  redColor?: string;
}

export interface CompactSession {
  id?: number;
  orchardId: string;
  orchardName: string;
  scouterName: string;
  currentIndex: number;
  records: Record<number, { pestId: string, timestamp: string, lat: number, lon: number, accuracy?: number, hardwareTimestamp?: number }>; // index 0-99
  pestCounts?: Record<string, number>; // orchard-level counts for 'count' measurement type pests
  pestLevels?: Record<string, string>; // orchard-level levels for 'level' measurement type pests
  totalInspectionPoints?: number; // denominator for percentage calculations; defaults to 100 if absent
  startTime: string;
  lastUpdated: string;
  isComplete: 0 | 1; // 0 for false, 1 for true
}

// ────────────────────────────────────────────────────────────────────────────
//  Spray session Field Mode sign-off — offline queue
// ────────────────────────────────────────────────────────────────────────────

export type SpraySignOffSyncStatus =
  | 'pending'
  | 'syncing'
  | 'synced'
  | 'failed'
  | 'quarantined';

export interface SpraySignOffPayload {
  // PPE moved upstream to /start in Phase 3 (track
  // spray_session_workflow_20260427); the new completion form does not
  // include it. Kept optional for backwards compatibility with sign-offs
  // queued by an older client revision still pending sync.
  ppeChecklist?: Array<{ item: string; checked: boolean }>;
  // startTime is optional on the wire (the backend treats it as a
  // retroactive override of session.startDate). The current completion
  // form always sends one.
  startTime?: string;
  endTime: string;
  wind: string;
  temperature: number;
  productsConsumed?: Array<{ productId: string; quantity: number }>;
}

/**
 * One queued Field Mode sign-off. Written when the operator submits the
 * completion form while offline or when a submission fails with a network
 * error; drained by syncPendingSprayCompletions on reconnect.
 */
export interface SpraySignOff {
  id?: number;
  sessionId: string;
  payload: SpraySignOffPayload;
  syncStatus: SpraySignOffSyncStatus;
  retryCount?: number;
  createdAt: string;
  lastAttemptAt?: string;
  lastError?: string;
}

/**
 * One cached hourly weather record kept locally by the PWA so that Field Mode
 * can categorise spray sign-offs while offline. Refreshed by the sync engine
 * from GET /api/v1/tenants/me/weather/recent and pruned to the last 3 hours.
 *
 * Track: automatic_weather_tracking_20260428 / Phase 4 / Task 3.
 */
export interface WeatherCacheEntry {
  /** ISO-8601 timestamp from the backend record — the natural primary key. */
  timestamp: string;
  temp?: number;
  windSpeed?: number;
  precipitation?: number;
  condition?: string;
  humidity?: number;
  pressure?: number;
  /** ISO-8601 wall-clock time when the entry was written into the cache. */
  cachedAt: string;
}

// ────────────────────────────────────────────────────────────────────────────
//  Spray session local cache + offline queues (Dexie v9)
//  Track: spray_session_workflow_20260427 / Phase 4.
// ────────────────────────────────────────────────────────────────────────────

export interface CachedSpraySessionProgramRef {
  _id: string;
  name: string;
}

/**
 * A spray session row mirrored from GET /sessions/my for offline reading.
 * Mirrors the wire shape used by OperatorSessionList — keep in lockstep
 * with sprayService.SpraySession but trimmed to fields the cache reader
 * actually needs (and with programId always populated).
 */
export interface CachedSpraySession {
  _id: string;
  tenantId: string;
  programId: CachedSpraySessionProgramRef;
  name?: string;
  orchardIds: string[];
  date: string;
  startDate?: string;
  endDate?: string;
  productsConsumed: Array<{ productId: string; quantity: number; plannedQuantity?: number }>;
  machineId?: string;
  operatorIds?: string[];
  status: 'planned' | 'in_progress';
  intensityLevel?: string;
  ppeChecklist?: Array<{ item: string; checked: boolean }>;
  createdAt: string;
  /** Local-only: when this row was last refreshed from the server. */
  cachedAt: string;
}

/**
 * Slim spray product row for the offline product catalog. Stores only
 * the fields the Field Mode sign-off form actually reads when joining
 * productsConsumed[].productId — name and dose unit. Cached opportunistically
 * by `getProductsWithCacheFallback` and warmed up by OperatorSessionList
 * so it's available when the operator drops offline mid-session.
 */
export interface CachedSprayProduct {
  _id: string;
  productName: string;
  dosePer100LUnit?: string;
  /** Local-only: when this row was last refreshed from the server. */
  cachedAt: string;
}

export type SpraySessionStartSyncStatus =
  | 'pending'
  | 'syncing'
  | 'synced'
  | 'failed'
  | 'quarantined';

export interface SpraySessionStartPayload {
  ppeChecklist: Array<{ item: string; checked: boolean }>;
  startedAt: string;
}

/**
 * One queued /start invocation. Written when the operator taps Start
 * Session while offline (or when an online attempt fails with a network
 * error); drained by spraySyncService alongside the completion queue.
 */
export interface SpraySessionStart {
  id?: number;
  sessionId: string;
  payload: SpraySessionStartPayload;
  syncStatus: SpraySessionStartSyncStatus;
  retryCount?: number;
  createdAt: string;
  lastAttemptAt?: string;
  lastError?: string;
}

/**
 * One non-retryable sync failure surfaced to the operator on next mount
 * of OperatorSessionList. Examples: a Complete payload that the server
 * rejects because the session was cancelled by an admin while the
 * operator was offline. The row is deleted when the user dismisses the
 * banner.
 */
export interface SpraySyncError {
  id?: number;
  sessionId: string;
  /** Stable code so the UI can map to translated copy. */
  errorCode: string;
  /** Server-supplied message (English). */
  message: string;
  createdAt: string;
}

// ────────────────────────────────────────────────────────────────────────────
//  Operator combo cache + offline queues (Dexie v11)
//  Track: multi_operator_spray_sessions / Phase 6 (Operator UI Redesign).
//
//  Per-combo (operator × session × machine) local cache mirroring the
//  /operator-sessions/my response, plus three queue/draft tables that
//  back the new combo-centric operator flow:
//    - operatorCombos:         cached combo rows for offline read.
//    - operatorComboStarts:    queued /start submissions.
//    - operatorComboCompletes: queued /complete | /end-early | /abandon
//                              submissions (discriminated by `action`).
//    - operatorComboDrafts:    per-combo in-progress form state.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Shape of the categorical weather snapshot the operator submits with
 * start/complete. `systemReported` carries the raw values the PWA pulled
 * from the weather cache at decision time so the audit trail can show
 * the operator's manual override (if any).
 */
export interface ComboWeatherSnapshot {
  temperatureCategory: string;
  windCategory: string;
  precipitationCategory: string;
  systemReported?: {
    temp: number;
    windSpeed: number;
    precipitation: number;
  };
  overrideComment?: string;
}

/**
 * One combo row mirrored from GET /operator-sessions/my for offline reading.
 * `parentId` and `parentDate` are denormalised flat strings for indexing —
 * Dexie compound indexes on object subfields are unreliable, so we lift
 * them to the top level alongside the nested `sessionId` reference.
 */
export interface CachedOperatorCombo {
  _id: string;
  tenantId: string;
  parentId: string;          // denormalised — top-level for index
  parentDate: string;        // denormalised — top-level for index
  sessionId: {
    _id: string;
    date: string;
    orchardIds: string[];
    programId: { _id: string; name: string };
    roundId?: string;
    status: string;
    name?: string;       // session display name when present
    seq?: string | number;
    intensityLevel?: IntensityLabel;
  };
  operatorId: string;
  machineId: string;
  /**
   * Phase 6b — per-combo spray intensity. Optional; when absent, reads
   * fall back to `sessionId.intensityLevel` via resolveComboIntensity().
   * Cached alongside `sessionId.intensityLevel` to keep the resolver
   * functional offline. Slated for removal alongside the parent field in
   * deferred-item 3.10's cleanup PR.
   */
  intensityLevel?: IntensityLabel;
  status: 'not_started' | 'in_progress' | 'completed' | 'incomplete' | 'cancelled';
  startedAt?: string;
  endedAt?: string;
  ppeChecklist: Array<{ item: string; checked: boolean; checkedAt: string | null }>;
  weatherSnapshots?: {
    start?: ComboWeatherSnapshot;
    end?: ComboWeatherSnapshot;
  };
  /** Local-only: when this row was last refreshed from the server. */
  cachedAt: string;
}

/**
 * One queued /start invocation for an operator combo. Carries the FULL
 * payload submitted to the backend so the drainer can replay it verbatim.
 */
export interface OperatorComboStart {
  localId?: number;
  comboId: string;
  payload: {
    startedAt?: string;
    weatherStart: ComboWeatherSnapshot;
    ppeChecks: Array<{ item: string; checked: boolean }>;
  };
  syncStatus: 'pending' | 'syncing' | 'failed' | 'quarantined';
  createdAt: string;
  lastError?: string;
}

/**
 * One queued completion-style submission for an operator combo. The
 * `action` field discriminates between /complete, /end-early, and
 * /abandon; the `payload` shape is the UNION of all three (not every
 * field is used by every action).
 */
export interface OperatorComboCompletion {
  localId?: number;
  comboId: string;
  action: 'complete' | 'end-early' | 'abandon';
  payload: {
    endTime?: string;
    tankCount?: number;
    weatherEnd?: ComboWeatherSnapshot;
    reason?: string;
    confirmedNoProductSprayed?: boolean;
  };
  syncStatus: 'pending' | 'syncing' | 'failed' | 'quarantined';
  createdAt: string;
  lastError?: string;
}

/**
 * One in-progress form draft for an operator combo (one draft per combo).
 * Keyed by `comboId` so that re-opening the same combo restores whatever
 * the operator had typed before they navigated away.
 */
export interface OperatorComboDraft {
  comboId: string;
  state: Partial<{
    ppeChecks: Record<string, boolean>;
    startTime: string;
    tankCount: string;
    endTime: string;
    windCategory: string;
    temperatureCategory: string;
    precipitationCategory: string;
    overrideComment: string;
    reason: string;
    confirmedNoProductSprayed: boolean;
    activeAction: 'complete' | 'end-early' | 'abandon' | null;
  }>;
  updatedAt: string;
}

// ────────────────────────────────────────────────────────────────────────────
//  Field Scout Mode — offline draft + sync queue (Dexie v12)
//  Track: field_scout_recording_mobile / Slice 2.
//
//  fieldScoutDrafts:  per-orchard in-progress recording state persisted
//                     between page loads. One draft per scout+orchard pair;
//                     replaced/merged on every point submission.
//  fieldScoutQueue:   batch payloads queued for POST /scouting/field-batch;
//                     drained by the field-scout sync engine on reconnect.
// ────────────────────────────────────────────────────────────────────────────

export interface FieldScoutDraft {
  id?: number;
  orchardId: string;
  orchardName: string;
  scouterName: string;
  currentPoint: number;        // 0-99
  maxExamined: number;         // examined = idx <= this
  activePestId: string;
  mode: 'solo' | 'all';
  infested: Record<string, number[]>;              // pestId -> infested point indices
  tallyInForce: Record<string, 'any' | '2+' | '3+'>;
  levels: Record<string, 0 | 1 | 2 | 3 | 4>;
  counts: Record<string, number>;
  photos: Record<number, number[]>;                // pointId -> mediaIds
  pointCoords?: Record<number, { latitude: number; longitude: number; accuracy: number; timestamp: string }>; // pointId -> GPS fix
  startTime: string;
  lastUpdated: string;
  isComplete: 0 | 1;
  queueUuid?: string;          // the round this finished draft became (clean-up)
}

export interface FieldScoutQueueItem {
  id?: number;
  uuid: string;
  payload: any;                                    // the /scouting/field-batch body
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed' | 'quarantined';
  retryCount?: number;
  createdAt: string;
  syncedAt?: string;                               // ISO time the round was confirmed (retention)
  tenantId?: string;                               // farm the round was recorded on
}

// ────────────────────────────────────────────────────────────────────────────
//  Nutrition foliar combo offline queues (Dexie v15)
//  Track: nutrition-slice3-foliar-sessions
//
//  nutritionComboStarts:    queued /start submissions for foliar combos.
//  nutritionComboCompletes: queued /complete submissions for foliar combos.
// ────────────────────────────────────────────────────────────────────────────

export interface NutritionComboStartQueueItem {
  localId?: number;
  comboId: string;
  payload: {
    startedAt: string;
    weatherStart?: ComboWeatherSnapshot;
  };
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed' | 'quarantined';
  retryCount?: number;
  createdAt: string;
  lastError?: string;
}

export interface NutritionComboCompleteQueueItem {
  localId?: number;
  comboId: string;
  payload: {
    endedAt: string;
    tankCount: number;
    // Optional (#506, D1): the server recomputes this itself and discards
    // whatever the client sends, so a queued row built from the field page
    // never carries it — see NutritionComboCompletePayload.
    productsConsumed?: Array<{ productId: string; quantity: number; unit?: string }>;
    weatherEnd?: ComboWeatherSnapshot;
  };
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed' | 'quarantined';
  retryCount?: number;
  createdAt: string;
  lastError?: string;
}

// ────────────────────────────────────────────────────────────────────────────
//  Nutrition soil session offline queues (Dexie v16)
//  Track: nutrition-slice4-soil-sessions
//
//  nutritionSoilStarts:    queued /start submissions for soil sessions.
//  nutritionSoilCompletes: queued /complete submissions for soil sessions.
// ────────────────────────────────────────────────────────────────────────────

export interface NutritionSoilStartQueueItem {
  localId?: number;
  sessionId: string;
  payload: {
    startedAt: string;
    operatorId: string;
  };
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed' | 'quarantined';
  retryCount?: number;
  createdAt: string;
  lastError?: string;
}

export interface NutritionSoilCompleteQueueItem {
  localId?: number;
  sessionId: string;
  payload: {
    endedAt: string;
    waterUsedL: number;
    products: Array<{ productId: string; quantity: number; unit?: string }>;
  };
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed' | 'quarantined';
  retryCount?: number;
  createdAt: string;
  lastError?: string;
}

// ────────────────────────────────────────────────────────────────────────────
//  Soil application GROUP offline queues + worklist cache (Dexie v19)
//  Issue #417 Slice 4 — the NEW soil stack.
//
//  Distinct from nutritionSoilStarts / nutritionSoilCompletes, which belong to
//  the LEGACY soil stack and POST to /nutrition/soil-sessions/*. Those are
//  removed in Slice 5 and must not be reused here.
// ────────────────────────────────────────────────────────────────────────────

export type SoilGroupSyncStatus =
  | 'pending' | 'syncing' | 'synced' | 'failed' | 'quarantined' | 'conflicted';

export interface SoilGroupStartQueueItem {
  localId?: number;
  sessionId: string;
  groupId: string;
  /** Human label, kept so a conflict banner can name the group offline. */
  label: string;
  payload: { startedAt: string; startKey: string };
  syncStatus: SoilGroupSyncStatus;
  retryCount?: number;
  createdAt: string;
  lastError?: string;
}

export interface SoilGroupCompleteQueueItem {
  localId?: number;
  sessionId: string;
  groupId: string;
  label: string;
  payload: {
    startedAt?: string;
    completedAt: string;
    ecReading?: number;
    signatureName?: string;
    captureChannel: 'field' | 'backcapture';
    completeKey: string;
  };
  syncStatus: SoilGroupSyncStatus;
  retryCount?: number;
  createdAt: string;
  lastError?: string;
}

/** One row of GET /soil-application-sessions/my, mirrored for offline reading. */
export interface CachedSoilMyGroup {
  id: string;                 // `${sessionId}:${groupId}`
  sessionId: string;
  groupId: string;
  label: string;
  productName: string;
  month: string;
  season: string;
  orchardNames: string[];
  plannedKg: number;
  /** Frozen at plan time. Optional: rows cached before U5 shipped lack them. */
  cultivars?: string[];
  treeCount?: number;
  areaHa?: number;
  status: 'not_started' | 'in_progress' | 'complete' | 'signed_off';
  startedAt?: string | null;
  /** Local-only: when this row was last refreshed from the server. */
  cachedAt: string;
}

// Desktop dual-mode paper transcription draft (Slice 3). Non-sequential:
// `enteredPoints` (NOT a maxExamined watermark) records which points have been
// transcribed; `examined = enteredPoints.length`. Stored in a SEPARATE table
// from mobile `fieldScoutDrafts` so the mobile flow's indexed lookups are never
// shadowed by a desktop draft.
export interface FieldScoutDeskDraft {
  id?: number;
  orchardId: string;
  orchardName: string;
  scouterName: string;          // as written on the paper sheet
  dateWalked: string;           // ISO date from the sheet
  mode: 'matrix' | 'point';
  activePestId: string;
  gridMode: 'solo' | 'all';
  enteredPoints: number[];      // 0-99; examined = length. A point is examined
                                // (entered) once it has any verdict or photo; an
                                // entered point's untouched pest reads 'clean'.
  infested: Record<string, number[]>;
  tallyInForce: Record<string, 'any' | '2+' | '3+'>;
  levels: Record<string, 0 | 1 | 2 | 3 | 4>;
  counts: Record<string, number>;
  photos: Record<number, number[]>;
  currentPoint: number;         // Point-mode inspector focus
  startTime: string;
  lastUpdated: string;
  isComplete: 0 | 1;
  queueUuid?: string;           // the round this finished draft became (clean-up)
}

export class CitrusSynchDB extends Dexie {
  scoutingSessions!: Table<ScoutingSession>;
  pests!: Table<Pest>;
  orchards!: Table<Orchard>;
  media!: Table<Media>;
  settings!: Table<AppSettings>;
  compactSessions!: Table<CompactSession>;
  dashboardSettings!: Table<DashboardSettings>;
  phenologyRecords!: Table<PhenologyRecord>;
  spraySignOffs!: Table<SpraySignOff>;
  weatherCache!: Table<WeatherCacheEntry>;
  spraySessions!: Table<CachedSpraySession, string>;
  spraySessionStarts!: Table<SpraySessionStart, number>;
  spraySyncErrors!: Table<SpraySyncError, number>;
  sprayProducts!: Table<CachedSprayProduct, string>;
  operatorCombos!: Table<CachedOperatorCombo, string>;
  operatorComboStarts!: Table<OperatorComboStart, number>;
  operatorComboCompletes!: Table<OperatorComboCompletion, number>;
  operatorComboDrafts!: Table<OperatorComboDraft, string>;
  fieldScoutDrafts!: Table<FieldScoutDraft>;
  fieldScoutQueue!: Table<FieldScoutQueueItem, number>;
  fieldScoutDeskDrafts!: Table<FieldScoutDeskDraft>;
  nutritionComboStarts!: Table<NutritionComboStartQueueItem, number>;
  nutritionComboCompletes!: Table<NutritionComboCompleteQueueItem, number>;
  nutritionSoilStarts!: Table<NutritionSoilStartQueueItem, number>;
  nutritionSoilCompletes!: Table<NutritionSoilCompleteQueueItem, number>;
  nutritionMySessions!: Table<NutritionMyWorkItem, string>;
  soilGroupStarts!: Table<SoilGroupStartQueueItem, number>;
  soilGroupCompletes!: Table<SoilGroupCompleteQueueItem, number>;
  soilMyGroups!: Table<CachedSoilMyGroup, string>;

  constructor() {
    super('CitrusSynchDB');
    this.version(5).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]'
    });
    this.version(6).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
    });
    this.version(7).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
    });
    // v8: added spraySignOffs table for Field Mode sign-off offline queue
    // (track operator_completion_20260412 / Phase 4).
    this.version(8).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
      spraySignOffs: '++id, sessionId, syncStatus, createdAt',
    });
    // v9: added weatherCache table for the PWA's 3-hour offline weather cache
    // (track automatic_weather_tracking_20260428 / Phase 4 / Task 3).
    // v9: spray session local cache + Start queue + sync-error surface
    // (track spray_session_workflow_20260427 / Phase 4).
    //   - spraySessions:       primary key on the server _id; compound
    //                          [_id+status] supports the cache reader's
    //                          'planned/in_progress' filters.
    //   - spraySessionStarts:  mirrors spraySignOffs shape so the drainer
    //                          can iterate both queues identically.
    //   - spraySyncErrors:     append-only, scoped per session; UI deletes
    //                          rows on dismiss.
    this.version(9).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
      spraySignOffs: '++id, sessionId, syncStatus, createdAt',
      weatherCache: '&timestamp, cachedAt',
      spraySessions: '_id, status, [_id+status], cachedAt',
      spraySessionStarts: '++id, sessionId, syncStatus, createdAt',
      spraySyncErrors: '++id, sessionId, createdAt',
    });
    // v10: spray product local cache. Field Mode's offline sign-off
    // form joins productsConsumed[].productId against this catalog to
    // render the human-readable productName + dosePer100LUnit. Without
    // it, an offline operator re-opening a queued sign-off sees raw
    // ObjectId strings.
    this.version(10).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
      spraySignOffs: '++id, sessionId, syncStatus, createdAt',
      spraySessions: '_id, status, [_id+status], cachedAt',
      spraySessionStarts: '++id, sessionId, syncStatus, createdAt',
      spraySyncErrors: '++id, sessionId, createdAt',
      sprayProducts: '_id, cachedAt',
    });
    // v11: operator combo cache + offline queues + per-combo drafts
    // (track multi_operator_spray_sessions / Phase 6 / Operator UI Redesign).
    //   - operatorCombos:          primary key on combo _id; compound
    //                              [parentId+operatorId] joins combos to
    //                              their parent session; `status` filters
    //                              active vs done; `parentDate` sorts by
    //                              parent session date. `parentId` /
    //                              `parentDate` are denormalised flat
    //                              strings — Dexie compound indexes on
    //                              object subfields are unreliable.
    //   - operatorComboStarts:     queued /start submissions; `localId`
    //                              auto-increments to differentiate from
    //                              the existing spraySessionStarts queue.
    //   - operatorComboCompletes:  queued /complete | /end-early |
    //                              /abandon submissions; `action`
    //                              discriminates the request.
    //   - operatorComboDrafts:     per-combo in-progress form state,
    //                              keyed by `comboId` (one draft per
    //                              combo).
    this.version(11).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
      spraySignOffs: '++id, sessionId, syncStatus, createdAt',
      spraySessions: '_id, status, [_id+status], cachedAt',
      spraySessionStarts: '++id, sessionId, syncStatus, createdAt',
      spraySyncErrors: '++id, sessionId, createdAt',
      sprayProducts: '_id, cachedAt',
      operatorCombos: '_id, [parentId+operatorId], status, parentDate',
      operatorComboStarts: '++localId, comboId, syncStatus, createdAt',
      operatorComboCompletes: '++localId, comboId, syncStatus, action, createdAt',
      operatorComboDrafts: 'comboId, updatedAt',
    });
    // v12: Field Scout Mode offline draft + sync queue (Slice 2).
    //   - fieldScoutDrafts:  per-orchard in-progress recording state;
    //                        compound [orchardId+scouterName+isComplete]
    //                        mirrors compactSessions for consistent filtering.
    //   - fieldScoutQueue:   batches queued for POST /scouting/field-batch;
    //                        `uuid` deduplicates replays; `syncStatus` drives
    //                        the drainer retry logic.
    this.version(12).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
      spraySignOffs: '++id, sessionId, syncStatus, createdAt',
      spraySessions: '_id, status, [_id+status], cachedAt',
      spraySessionStarts: '++id, sessionId, syncStatus, createdAt',
      spraySyncErrors: '++id, sessionId, createdAt',
      sprayProducts: '_id, cachedAt',
      operatorCombos: '_id, [parentId+operatorId], status, parentDate',
      operatorComboStarts: '++localId, comboId, syncStatus, createdAt',
      operatorComboCompletes: '++localId, comboId, syncStatus, action, createdAt',
      operatorComboDrafts: 'comboId, updatedAt',
      fieldScoutDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      fieldScoutQueue: '++id, uuid, syncStatus, createdAt',
    });
    // v13: Field Scout Mode desktop dual-mode draft (Slice 3).
    //   - fieldScoutDeskDrafts: per-orchard desktop transcription draft;
    //     SEPARATE from fieldScoutDrafts so mobile's [orchardId+scouterName+
    //     isComplete] .first() can never return a desktop draft.
    this.version(13).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
      spraySignOffs: '++id, sessionId, syncStatus, createdAt',
      spraySessions: '_id, status, [_id+status], cachedAt',
      spraySessionStarts: '++id, sessionId, syncStatus, createdAt',
      spraySyncErrors: '++id, sessionId, createdAt',
      sprayProducts: '_id, cachedAt',
      operatorCombos: '_id, [parentId+operatorId], status, parentDate',
      operatorComboStarts: '++localId, comboId, syncStatus, createdAt',
      operatorComboCompletes: '++localId, comboId, syncStatus, action, createdAt',
      operatorComboDrafts: 'comboId, updatedAt',
      fieldScoutDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      fieldScoutQueue: '++id, uuid, syncStatus, createdAt',
      fieldScoutDeskDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
    });
    // v14: Nutrition soil sign-off offline queue (Slice 3). Batched POSTs to
    //      /nutrition/soil/sheets/:id/signoff; `uuid` deduplicates replays.
    this.version(14).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
      spraySignOffs: '++id, sessionId, syncStatus, createdAt',
      spraySessions: '_id, status, [_id+status], cachedAt',
      spraySessionStarts: '++id, sessionId, syncStatus, createdAt',
      spraySyncErrors: '++id, sessionId, createdAt',
      sprayProducts: '_id, cachedAt',
      operatorCombos: '_id, [parentId+operatorId], status, parentDate',
      operatorComboStarts: '++localId, comboId, syncStatus, createdAt',
      operatorComboCompletes: '++localId, comboId, syncStatus, action, createdAt',
      operatorComboDrafts: 'comboId, updatedAt',
      fieldScoutDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      fieldScoutQueue: '++id, uuid, syncStatus, createdAt',
      fieldScoutDeskDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      nutritionSignoffQueue: '++id, sheetId, syncStatus, createdAt',
    });
    // v15: Nutrition foliar combo offline queues (Slice 3 — foliar sessions).
    //   - nutritionComboStarts:    queued /start submissions for foliar combos;
    //                              `localId` auto-increments; `comboId` links
    //                              to the NutritionOperatorSession._id on the
    //                              server; `syncStatus` drives the drainer.
    //   - nutritionComboCompletes: queued /complete submissions for foliar
    //                              combos; same shape as starts.
    this.version(15).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
      spraySignOffs: '++id, sessionId, syncStatus, createdAt',
      spraySessions: '_id, status, [_id+status], cachedAt',
      spraySessionStarts: '++id, sessionId, syncStatus, createdAt',
      spraySyncErrors: '++id, sessionId, createdAt',
      sprayProducts: '_id, cachedAt',
      operatorCombos: '_id, [parentId+operatorId], status, parentDate',
      operatorComboStarts: '++localId, comboId, syncStatus, createdAt',
      operatorComboCompletes: '++localId, comboId, syncStatus, action, createdAt',
      operatorComboDrafts: 'comboId, updatedAt',
      fieldScoutDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      fieldScoutQueue: '++id, uuid, syncStatus, createdAt',
      fieldScoutDeskDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      nutritionSignoffQueue: '++id, sheetId, syncStatus, createdAt',
      nutritionComboStarts: '++localId, comboId, syncStatus, createdAt',
      nutritionComboCompletes: '++localId, comboId, syncStatus, createdAt',
    });
    // v16: Nutrition soil session offline queues (Slice 4 — soil sessions).
    //   - nutritionSoilStarts:    queued /start submissions for soil sessions;
    //                             `localId` auto-increments; `sessionId` links
    //                             to the NutritionSession._id on the server;
    //                             `syncStatus` drives the drainer.
    //   - nutritionSoilCompletes: queued /complete submissions for soil sessions;
    //                             same shape as starts but carries actuals
    //                             (waterUsedL + products consumed).
    this.version(16).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
      spraySignOffs: '++id, sessionId, syncStatus, createdAt',
      spraySessions: '_id, status, [_id+status], cachedAt',
      spraySessionStarts: '++id, sessionId, syncStatus, createdAt',
      spraySyncErrors: '++id, sessionId, createdAt',
      sprayProducts: '_id, cachedAt',
      operatorCombos: '_id, [parentId+operatorId], status, parentDate',
      operatorComboStarts: '++localId, comboId, syncStatus, createdAt',
      operatorComboCompletes: '++localId, comboId, syncStatus, action, createdAt',
      operatorComboDrafts: 'comboId, updatedAt',
      fieldScoutDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      fieldScoutQueue: '++id, uuid, syncStatus, createdAt',
      fieldScoutDeskDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      nutritionSignoffQueue: '++id, sheetId, syncStatus, createdAt',
      nutritionComboStarts: '++localId, comboId, syncStatus, createdAt',
      nutritionComboCompletes: '++localId, comboId, syncStatus, createdAt',
      nutritionSoilStarts: '++localId, sessionId, syncStatus, createdAt',
      nutritionSoilCompletes: '++localId, sessionId, syncStatus, createdAt',
    });
    // v17: Nutrition My-Sessions worklist cache (Slice 5).
    //   - nutritionMySessions: flat worklist rows from GET /nutrition/sessions/my;
    //     primary key on `id` (comboId for foliar, sessionId for soil); `kind`
    //     and `status` are indexed for client-side filtering without a full scan.
    this.version(17).stores({
      scoutingSessions: '++id, uuid, scouterName, orchardName, date, syncStatus, source',
      pests: 'id, name',
      orchards: 'id, orchardName',
      media: '++id, sessionId',
      settings: 'id',
      compactSessions: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      dashboardSettings: 'id',
      phenologyRecords: '++id, orchardId, syncStatus',
      spraySignOffs: '++id, sessionId, syncStatus, createdAt',
      spraySessions: '_id, status, [_id+status], cachedAt',
      spraySessionStarts: '++id, sessionId, syncStatus, createdAt',
      spraySyncErrors: '++id, sessionId, createdAt',
      sprayProducts: '_id, cachedAt',
      operatorCombos: '_id, [parentId+operatorId], status, parentDate',
      operatorComboStarts: '++localId, comboId, syncStatus, createdAt',
      operatorComboCompletes: '++localId, comboId, syncStatus, action, createdAt',
      operatorComboDrafts: 'comboId, updatedAt',
      fieldScoutDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      fieldScoutQueue: '++id, uuid, syncStatus, createdAt',
      fieldScoutDeskDrafts: '++id, orchardId, scouterName, isComplete, [orchardId+scouterName+isComplete]',
      nutritionSignoffQueue: '++id, sheetId, syncStatus, createdAt',
      nutritionComboStarts: '++localId, comboId, syncStatus, createdAt',
      nutritionComboCompletes: '++localId, comboId, syncStatus, createdAt',
      nutritionSoilStarts: '++localId, sessionId, syncStatus, createdAt',
      nutritionSoilCompletes: '++localId, sessionId, syncStatus, createdAt',
      nutritionMySessions: 'id, kind, status',
    });
    // v18: drop the orphaned nutritionSignoffQueue table (legacy soil-signoff
    // writer removed in redesign Slice 6). In Dexie a table is deleted by
    // setting its store spec to `null` in a newer version; tables NOT mentioned
    // here carry over unchanged from v17.
    this.version(18).stores({
      nutritionSignoffQueue: null,
    });
    // v19: soil application GROUP queues + worklist cache (redesign Slice 4).
    // Tables NOT mentioned here carry over unchanged from v18.
    this.version(19).stores({
      soilGroupStarts:    '++localId, sessionId, groupId, syncStatus, createdAt',
      soilGroupCompletes: '++localId, sessionId, groupId, syncStatus, createdAt',
      soilMyGroups:       'id, sessionId, status',
    });
  }
}

export const db = new CitrusSynchDB();
